import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ContactModal, PrivacyPolicyModal } from '@joxpg/akretrix-ui';
import '@joxpg/akretrix-ui/styles.css';
import {
  Shield,
  ArrowRight,
  CheckCircle2,
  Award,
  Zap,
  ShieldCheck,
  Users,
  Star,
  GitBranch,
  FileCheck,
  Lock,
  Sparkles,
  Globe,
  ChevronRight,
  BookOpen,
  TrendingUp,
  Building2,
  Pencil,
  MessageSquare,
  Layers,
  ClipboardList,
  Sun,
  Moon,
  Headphones
} from 'lucide-react';

interface LandingPageProps {
  onGoToAuth: (tab?: 'signin' | 'signup') => void;
  theme?: 'dark' | 'light';
  setTheme?: (t: 'dark' | 'light') => void;
}

const COURSES = [
  {
    id: 'COURSE-SG-SST-01',
    icon: ShieldCheck,
    iconColor: '#10b981',
    iconBg: 'rgba(16, 185, 129, 0.15)',
    borderColor: 'rgba(16, 185, 129, 0.25)',
    title: { es: 'SG-SST: Seguridad y Salud en el Trabajo', en: 'OHS Management System (SG-SST)' },
    desc: {
      es: 'Decreto 1072 & Res. 0312 — Matriz IPEVR, COPASST, gestión del riesgo y brigadas de emergencia.',
      en: 'Decreto 1072 & Res. 0312 — IPEVR risk matrix, COPASST, hazard controls and emergency brigades.'
    },
    level: { es: 'Obligatorio • Colombia', en: 'Mandatory • Colombia' },
    tag: 'SG-SST',
  },
  {
    id: 'COURSE-GDPR-01',
    icon: Lock,
    iconColor: '#3b82f6',
    iconBg: 'rgba(59, 130, 246, 0.15)',
    borderColor: 'rgba(59, 130, 246, 0.25)',
    title: { es: 'RGPD / GDPR: Protección de Datos Personales', en: 'GDPR / Data Protection & Privacy Rights' },
    desc: {
      es: 'Bases legales del tratamiento, derechos ARCO, notificación de brechas en 72h y Registro de Actividades.',
      en: 'Lawful bases, data subject rights, 72h breach notification and Record of Processing Activities.'
    },
    level: { es: 'Obligatorio • Global', en: 'Mandatory • Global' },
    tag: 'GDPR',
  },
  {
    id: 'COURSE-SARLAFT-01',
    icon: TrendingUp,
    iconColor: '#f59e0b',
    iconBg: 'rgba(245, 158, 11, 0.15)',
    borderColor: 'rgba(245, 158, 11, 0.25)',
    title: { es: 'SARLAFT / AML: Prevención del Lavado de Activos', en: 'SARLAFT / AML: Money Laundering Prevention' },
    desc: {
      es: 'Señales de alerta, reporte de operaciones sospechosas (ROS), beneficiario real y matrices LAFT.',
      en: 'Red flags, suspicious transaction reports, Ultimate Beneficial Owner verification and LAFT matrices.'
    },
    level: { es: 'Obligatorio • LatAm / GAFI', en: 'Mandatory • LatAm / FATF' },
    tag: 'SARLAFT',
  },
  {
    id: 'COURSE-CYBERSEC-01',
    icon: Zap,
    iconColor: '#06b6d4',
    iconBg: 'rgba(6, 182, 212, 0.15)',
    borderColor: 'rgba(6, 182, 212, 0.25)',
    title: { es: 'Ciberseguridad & Concienciación (ISO 27001)', en: 'Cybersecurity Awareness (ISO 27001 / NIST)' },
    desc: {
      es: 'Phishing, ransomware, MFA, Zero Trust y gestión de incidentes de seguridad bajo ISO 27001.',
      en: 'Phishing, ransomware defense, MFA, Zero Trust architecture and ISO 27001 incident response.'
    },
    level: { es: 'Obligatorio • Global', en: 'Mandatory • Global' },
    tag: 'CYB',
  },
  {
    id: 'COURSE-CODEOFCONDUCT-01',
    icon: Users,
    iconColor: '#8b5cf6',
    iconBg: 'rgba(139, 92, 246, 0.15)',
    borderColor: 'rgba(139, 92, 246, 0.25)',
    title: { es: 'Código de Conducta y Prevención del Acoso (Ley 1010)', en: 'Code of Conduct & Harassment Prevention (Ley 1010)' },
    desc: {
      es: 'Conductas protegidas, canal de denuncia, Comité de Convivencia y protocolo de investigación.',
      en: 'Protected conduct, whistleblower channels, Convivencia Committee and investigation protocol.'
    },
    level: { es: 'Obligatorio • Colombia', en: 'Mandatory • Colombia' },
    tag: 'ÉTICA',
  },
];

const STATS = [
  { value: '5', label: { es: 'Cursos Normativos Obligatorios', en: 'Mandatory Compliance Courses' } },
  { value: '80%', label: { es: 'Umbral de Aprobación Certificado', en: 'Certified Passing Threshold' } },
  { value: '365d', label: { es: 'Vigencia de Credencial Open Badges 3.0', en: 'Open Badges 3.0 Credential Vigency' } },
  { value: '< 24h', label: { es: 'Onboarding Empresarial Completo', en: 'Full Enterprise Onboarding' } },
];

const METHODOLOGY = [
  {
    icon: GitBranch,
    color: '#f59e0b',
    title: { es: 'Simuladores de Crisis Ramificados', en: 'Branching Crisis Simulators' },
    desc: {
      es: 'Decisiones que afectan en tiempo real el Riesgo Legal, Presupuesto y Reputación corporativa. No hay respuestas pasivas.',
      en: 'Every choice alters your Legal Risk, Budget, and Reputation in real-time. No passive "Next" clicking.'
    },
  },
  {
    icon: Sparkles,
    color: '#8b5cf6',
    title: { es: 'Recompensas Misteriosas de Dopamina', en: 'Dopamine Mystery Loot Boxes' },
    desc: {
      es: 'Recompensas aleatorias (toolkits ISO, skins de insignia, títulos de élite) al resolver cada dilema de crisis.',
      en: 'Randomized rewards (ISO toolkits, badge skins, elite titles) released on each crisis resolution.'
    },
  },
  {
    icon: Award,
    color: '#10b981',
    title: { es: 'Credenciales Open Badges 3.0 Verificables', en: 'Verifiable Open Badges 3.0 Credentials' },
    desc: {
      es: 'Insignias criptográficas W3C con vigencia de 365 días, verificables públicamente por empleadores y entidades de control.',
      en: 'W3C cryptographic badges with 365-day vigency, publicly verifiable by employers and regulatory authorities.'
    },
  },
  {
    icon: TrendingUp,
    color: '#3b82f6',
    title: { es: 'Progresión Tipo Árbol de Habilidades', en: 'Skill Tree Progression Map' },
    desc: {
      es: 'Mapa visual de hitos desbloqueables: fundamentos → simulador → evaluación → certificación. Escasez y competencia integradas.',
      en: 'Visual node map: foundations → crisis simulator → evaluation → Open Badge. Scarcity and mastery built-in.'
    },
  },
];

export const LandingPage: React.FC<LandingPageProps> = ({ onGoToAuth, theme = 'dark', setTheme }) => {
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [contactService, setContactService] = useState('elearning-platform');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>

      {/* ── TOP NAVIGATION BAR ───────────────────────────────────────────── */}
      <nav
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 32px',
          height: '64px',
          background: 'rgba(11, 15, 25, 0.85)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid var(--border-color)',
        }}
      >
        {/* Logo / Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '9px',
              background: 'var(--gradient-brand)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'var(--shadow-glow)',
            }}
          >
            <Shield size={19} color="#ffffff" />
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '17px', letterSpacing: '-0.03em', color: 'var(--text-primary)', lineHeight: 1.1 }}>
              e-learning <span style={{ color: 'var(--accent-primary)', fontSize: '11px', fontWeight: 600 }}>by akretrix</span>
            </div>
            <div style={{ fontSize: '9.5px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
              {t('Cumplimiento Corporativo', 'Corporate Compliance')}
            </div>
          </div>
        </div>

        {/* Nav Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Language Switcher */}
          <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', padding: '2px', marginRight: '4px' }}>
            <Globe size={14} style={{ margin: '0 4px', color: 'var(--text-muted)' }} />
            <button
              onClick={() => setLang('es')}
              style={{
                background: lang === 'es' ? 'var(--accent-primary)' : 'transparent',
                color: lang === 'es' ? '#ffffff' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 6px',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              ES
            </button>
            <button
              onClick={() => setLang('en')}
              style={{
                background: lang === 'en' ? 'var(--accent-primary)' : 'transparent',
                color: lang === 'en' ? '#ffffff' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 6px',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              EN
            </button>
          </div>

          {/* Theme Switcher */}
          {setTheme && (
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-primary)',
                padding: '6px 10px',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '6px'
              }}
              title={theme === 'dark' ? t('Modo Claro', 'Light Mode') : t('Modo Oscuro', 'Dark Mode')}
            >
              {theme === 'dark' ? <Sun size={15} color="#f59e0b" /> : <Moon size={15} color="#8b5cf6" />}
            </button>
          )}
          <button
            onClick={() => onGoToAuth()}
            style={{
              background: 'none',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
              padding: '7px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            {t('Iniciar Sesión', 'Sign In')}
          </button>

          <button
            onClick={() => onGoToAuth('signup')}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              border: 'none',
              color: '#ffffff',
              padding: '8px 18px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 0 18px rgba(59, 130, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'opacity 0.15s ease',
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.88'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            {t('Registrarme', 'Sign Up')}
          </button>

          <button
            onClick={() => {
              setContactService('elearning-platform');
              setIsContactOpen(true);
            }}
            style={{
              background: 'rgba(6, 182, 212, 0.12)',
              border: '1px solid rgba(6, 182, 212, 0.35)',
              color: '#06b6d4',
              padding: '7px 14px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(6, 182, 212, 0.22)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(6, 182, 212, 0.12)'; }}
          >
            <Headphones size={15} />
            {t('Contacto', 'Contact Us')}
          </button>

          <button
            onClick={() => navigate('/verify')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              padding: '7px 12px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              transition: 'color 0.15s ease',
            }}
            onMouseOver={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
            onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
          >
            <FileCheck size={14} />
            {t('Verificar Insignia', 'Verify Badge')}
          </button>
        </div>
      </nav>

      {/* ── HERO SECTION ─────────────────────────────────────────────────── */}
      <section
        style={{
          minHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '80px 24px 60px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Ambient background radials */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          <div style={{ position: 'absolute', top: '10%', left: '15%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', top: '30%', right: '10%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: '10%', left: '35%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%)', borderRadius: '50%' }} />
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '860px' }}>
          {/* Pre-headline trust badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 14px',
              borderRadius: '20px',
              background: 'rgba(59, 130, 246, 0.12)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              color: '#93c5fd',
              fontSize: '12px',
              fontWeight: 700,
              marginBottom: '28px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            <Shield size={14} />
            {t('White-Label LMS · Plataforma Certificada B2B Open Badges 3.0 · LATAM & Global', 'White-Label LMS · Open Badges 3.0 · LATAM & Global')}
          </div>

          {/* Main headline */}
          <h1
            style={{
              fontSize: 'clamp(38px, 6vw, 72px)',
              fontWeight: 900,
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              marginBottom: '24px',
              fontFamily: 'var(--font-heading)',
            }}
          >
            {lang === 'es' ? (
              <>La Forma Más Efectiva de{' '}<span style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Certificar el Cumplimiento</span>{' '}Corporativo.</>
            ) : (
              <>The Most Effective Way to{' '}<span style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Certify Corporate Compliance.</span></>
            )}
          </h1>

          {/* Sub-headline */}
          <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: '680px', margin: '0 auto 40px', fontWeight: 400 }}>
            {t(
              'No más capacitaciones pasivas. Construimos simuladores de crisis inmersivos, escenarios de dilema ético y recompensas psicológicas que garantizan retención, no solo asistencia.',
              'No more passive slide-clicking. We build immersive crisis simulators, ethical dilemma scenarios, and psychological reward loops that guarantee true mastery—not just attendance records.'
            )}
          </p>

          {/* Primary CTAs */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              style={{
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 800,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                boxShadow: '0 0 32px rgba(59, 130, 246, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onClick={() => onGoToAuth('signup')}
            >
              {t('Comenzar Gratis', 'Start for Free')}
              <ArrowRight size={18} />
            </button>

            <button
              className="btn btn-secondary"
              style={{ padding: '16px 28px', fontSize: '16px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}
              onClick={() => onGoToAuth('signin')}
            >
              <BookOpen size={16} />
              {t('Iniciar Sesión', 'Sign In')}
            </button>
          </div>

          {/* Social proof micro-text */}
          <p style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <CheckCircle2 size={13} color="#10b981" />
            {t('Sin tarjeta de crédito · Onboarding corporativo en < 5 minutos', 'No credit card · Corporate onboarding in < 5 minutes')}
          </p>
        </div>
      </section>

      {/* ── STATS STRIP ──────────────────────────────────────────────────── */}
      <div style={{ borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', padding: '32px 24px', background: 'var(--bg-secondary)' }}>
        <div
          style={{
            maxWidth: '900px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '24px',
            textAlign: 'center',
          }}
        >
          {STATS.map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: '32px', fontWeight: 900, color: 'var(--accent-primary)', fontFamily: 'var(--font-heading)' }}>{s.value}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.4px', marginTop: '4px' }}>{s.label[lang]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── METHODOLOGY: WHAT WE DO ──────────────────────────────────────── */}
      <section style={{ padding: '100px 24px', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 12px', borderRadius: '16px', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#fbbf24', fontSize: '12px', fontWeight: 700, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <Sparkles size={14} />
            {t('Nuestra Metodología (Octalysis Framework)', 'Our Methodology (Octalysis Framework)')}
          </div>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.025em', marginBottom: '16px' }}>
            {t('Diseñado para Mantener al Aprendiz en Estado de', 'Engineered to Keep Every Learner in a State of')}{' '}
            <span style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              {t('Flow Total.', 'Total Flow.')}
            </span>
          </h2>
          <p style={{ fontSize: '16px', color: 'var(--text-secondary)', maxWidth: '620px', margin: '0 auto', lineHeight: 1.6 }}>
            {t(
              'Cada módulo es un dilema real. Cada decisión tiene consecuencias. Aprende haciendo, no memorizando.',
              "Every module is a real dilemma. Every decision has consequences. Learn by doing, not memorizing."
            )}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
          {METHODOLOGY.map((m, i) => (
            <div
              key={i}
              className="glass-card"
              style={{
                padding: '28px',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                transition: 'all 0.2s ease',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '14px',
                  background: `${m.color}22`,
                  border: `1px solid ${m.color}44`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                <m.icon size={22} color={m.color} />
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 800, marginBottom: '10px', color: 'var(--text-primary)' }}>
                {m.title[lang]}
              </h3>
              <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {m.desc[lang]}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── COURSE SHOWCASE ──────────────────────────────────────────────── */}
      <section style={{ padding: '80px 24px 100px', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 12px', borderRadius: '16px', background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#34d399', fontSize: '12px', fontWeight: 700, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <FileCheck size={14} />
              {t('Catálogo de Cursos Obligatorios 2026', 'Mandatory Compliance Course Catalog 2026')}
            </div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.025em', marginBottom: '14px' }}>
              {t('5 Cursos. 1 Plataforma. Cero Riesgos Legales.', '5 Courses. 1 Platform. Zero Legal Risk.')}
            </h2>
            <p style={{ fontSize: '15px', color: 'var(--text-secondary)', maxWidth: '560px', margin: '0 auto', lineHeight: 1.6 }}>
              {t(
                'Todos los cursos son anuales, obligatorios por ley, y entregan credenciales criptográficas verificables por empleadores y autoridades regulatorias.',
                'All courses are annual, legally mandated, and issue W3C cryptographic credentials verifiable by employers and regulatory authorities.'
              )}
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
            {COURSES.map((c) => (
              <div
                key={c.id}
                className="glass-card"
                style={{
                  padding: '24px',
                  borderRadius: 'var(--radius-lg)',
                  border: `1px solid ${c.borderColor}`,
                  background: `linear-gradient(135deg, ${c.iconBg} 0%, var(--bg-card) 60%)`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '16px',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: c.iconBg, border: `1px solid ${c.borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <c.icon size={22} color={c.iconColor} />
                    </div>
                    <span style={{ fontSize: '10.5px', fontWeight: 800, color: c.iconColor, background: c.iconBg, padding: '3px 10px', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      {c.tag}
                    </span>
                  </div>

                  <h3 style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px', lineHeight: 1.3 }}>
                    {c.title[lang]}
                  </h3>

                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: c.iconColor, fontWeight: 700, marginBottom: '10px', background: c.iconBg, padding: '2px 8px', borderRadius: '8px' }}>
                    <Star size={11} fill={c.iconColor} />
                    {c.level[lang]}
                  </div>

                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                    {c.desc[lang]}
                  </p>
                </div>

                <button
                  onClick={() => onGoToAuth('signup')}
                  className="btn"
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: c.iconBg,
                    border: `1px solid ${c.borderColor}`,
                    color: c.iconColor,
                    borderRadius: 'var(--radius-md)',
                    fontSize: '13px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                  onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                >
                  {t('Inscribirme al Curso', 'Enroll Now')}
                  <ChevronRight size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── B2B ENTERPRISE TRUST SECTION ─────────────────────────────────── */}
      <section style={{ padding: '100px 24px', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 12px', borderRadius: '16px', background: 'rgba(139, 92, 246, 0.12)', border: '1px solid rgba(139, 92, 246, 0.3)', color: '#c084fc', fontSize: '12px', fontWeight: 700, marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <Building2 size={14} />
          {t('Para Líderes de Recursos Humanos & Cumplimiento', 'For HR Leaders & Compliance Officers')}
        </div>

        <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.025em', marginBottom: '20px' }}>
          {lang === 'es' ? (
            <>Cumplimiento Normativo{' '}<span style={{ background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Sin Fricciones Operativas.</span></>
          ) : (
            <>Regulatory Compliance{' '}<span style={{ background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Without Operational Friction.</span></>
          )}
        </h2>

        <p style={{ fontSize: '16px', color: 'var(--text-secondary)', maxWidth: '640px', margin: '0 auto 40px', lineHeight: 1.6 }}>
          {t(
            'Panel B2B en tiempo real, carga masiva de empleados vía CSV, auto-inscripción y reportes de cumplimiento exportables para auditorías del Ministerio de Trabajo, SFC o SARLAFT.',
            'Real-time B2B admin dashboard, bulk employee CSV upload, auto-enrollment, and compliance reports exportable for Ministry of Labor, SFC, or SARLAFT regulatory audits.'
          )}
        </p>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '36px' }}>
          {[
            { icon: Users, label: { es: 'Onboarding Masivo CSV', en: 'Bulk CSV Onboarding' } },
            { icon: Award, label: { es: 'Credenciales Open Badges 3.0', en: 'Open Badges 3.0 Credentials' } },
            { icon: Globe, label: { es: 'LMS 100% Marca Blanca', en: '100% White-Label LMS' } },
            { icon: ShieldCheck, label: { es: 'Dashboard de Cumplimiento en Vivo', en: 'Live Compliance Dashboard' } },
          ].map((feature, i) => (
            <div
              key={i}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '20px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}
            >
              <feature.icon size={15} color="var(--accent-primary)" />
              {feature.label[lang]}
            </div>
          ))}
        </div>

        <button
          className="btn btn-primary"
          style={{ padding: '16px 32px', fontSize: '16px', fontWeight: 800, borderRadius: '12px', background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)', boxShadow: '0 0 32px rgba(139, 92, 246, 0.35)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          onClick={() => onGoToAuth('signup')}
        >
          <Building2 size={18} />
          {t('Solicitar Demo Empresarial', 'Request Enterprise Demo')}
          <ArrowRight size={16} />
        </button>
      </section>

      {/* ── WHITE-LABEL LMS SECTION ──────────────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 12px', borderRadius: '16px', background: 'rgba(6, 182, 212, 0.12)', border: '1px solid rgba(6, 182, 212, 0.3)', color: '#22d3ee', fontSize: '12px', fontWeight: 700, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Star size={14} />
              {t('Marca Blanca · B2B Enterprise', 'White-Label · B2B Enterprise')}
            </div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.025em', marginBottom: '16px' }}>
              {lang === 'es' ? (
                <>Tu Marca. Tu Paleta.{' '}<span style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Nuestra Plataforma.</span></>
              ) : (
                <>Your Brand. Your Colors.{' '}<span style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Our Platform.</span></>
              )}
            </h2>
            <p style={{ fontSize: '16px', color: 'var(--text-secondary)', maxWidth: '640px', margin: '0 auto', lineHeight: 1.6 }}>
              {t(
                'Ofrece la plataforma de capacitación a tus clientes empresariales con su propio logotipo, colores corporativos y dominio personalizado. Cero rastro de AkreTrix si así lo prefieren.',
                'Deliver the training platform to your enterprise clients under their own logo, corporate color palette, and custom domain. Zero AkreTrix branding if they prefer it that way.'
              )}
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {[
              {
                icon: Globe,
                color: '#06b6d4',
                title: { es: 'Dominio Corporativo Propio', en: 'Custom Corporate Domain' },
                desc: { es: 'Alojen la plataforma en su propio subdominio (ej. capacitacion.empresa.com). Sin referencias a elearning.akretrix.com.', en: 'Host the platform on the client\'s own subdomain (e.g. training.company.com). No references to elearning.akretrix.com.' },
              },
              {
                icon: Star,
                color: '#f59e0b',
                title: { es: 'Branding Corporativo Total', en: 'Full Corporate Branding' },
                desc: { es: 'Logo, paleta de colores y tipografía de la empresa cliente en el Navbar, diplomas y correos automáticos de bienvenida.', en: 'Client company logo, color palette, and typography in the Navbar, diplomas, and automated welcome emails.' },
              },
              {
                icon: Award,
                color: '#10b981',
                title: { es: 'Insignias Co-Branded Open Badges 3.0', en: 'Co-Branded Open Badges 3.0' },
                desc: { es: 'Las credenciales W3C llevan el sello oficial de la empresa cliente junto a la firma criptográfica SHA-256, verificable por terceros.', en: 'W3C credentials carry the client company\'s official seal alongside the SHA-256 cryptographic signature, verifiable by third parties.' },
              },
              {
                icon: Building2,
                color: '#8b5cf6',
                title: { es: 'Panel Administrativo Privado', en: 'Private Admin Dashboard' },
                desc: { es: 'El panel B2B de gestión de empleados y reportes de cumplimiento refleja únicamente la marca del cliente. Sin rastros de AkreTrix.', en: 'The B2B employee management and compliance reporting panel reflects only the client\'s brand. No AkreTrix traces.' },
              },
            ].map((item, i) => (
              <div key={i} className="glass-card" style={{ padding: '28px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-color)' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: `${item.color}22`, border: `1px solid ${item.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                  <item.icon size={22} color={item.color} />
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 800, marginBottom: '10px', color: 'var(--text-primary)' }}>{item.title[lang]}</h3>
                <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.desc[lang]}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '48px', textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.25)', fontSize: '13px', color: 'var(--text-secondary)' }}>
              <CheckCircle2 size={14} color="#22d3ee" />
              {t('Disponible para clientes Enterprise · Configuración en 24h · SLA 99.9%', 'Available for Enterprise clients · Setup in 24h · 99.9% SLA')}
            </div>
          </div>
        </div>
      </section>

      {/* ── CUSTOM COURSE AUTHORING SECTION ───────────────────────── */}
      <section style={{ padding: '100px 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 12px', borderRadius: '16px', background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', fontSize: '12px', fontWeight: 700, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Pencil size={14} />
              {t('Desarrollo de Cursos a Medida · Servicio Enterprise', 'Custom Course Authoring · Enterprise Service')}
            </div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.025em', marginBottom: '16px' }}>
              {lang === 'es' ? (
                <>Cursos Diseñados{' '}<span style={{ background: 'linear-gradient(135deg, #ef4444, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Exactamente Para Ti.</span></>
              ) : (
                <>Courses Designed{' '}<span style={{ background: 'linear-gradient(135deg, #ef4444, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Exactly For You.</span></>
              )}
            </h2>
            <p style={{ fontSize: '16px', color: 'var(--text-secondary)', maxWidth: '640px', margin: '0 auto', lineHeight: 1.6 }}>
              {t(
                '¿Tienes procesos internos, políticas propias o normativas sectoriales específicas? Nuestro equipo diseña y produce cursos 100% personalizados: desde el guión hasta la insignia Open Badges 3.0.',
                'Do you have internal processes, proprietary policies, or industry-specific regulations? Our team designs and produces 100% custom courses: from script to Open Badges 3.0 credential.'
              )}
            </p>
          </div>

          {/* Two-column layout: steps + CTA card */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px', alignItems: 'start' }}>
            {/* Step-by-step process */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  step: '01',
                  icon: MessageSquare,
                  color: '#ef4444',
                  title: { es: 'Diagnóstico de Necesidades', en: 'Needs Discovery' },
                  desc: { es: 'Reunimos los requerimientos legales, procesos internos y perfiles de aprendiz de tu empresa.', en: 'We gather your legal requirements, internal processes, and learner profiles.' },
                },
                {
                  step: '02',
                  icon: ClipboardList,
                  color: '#f59e0b',
                  title: { es: 'Diseño Instruccional & Guión', en: 'Instructional Design & Script' },
                  desc: { es: 'Creamos el guión, los escenarios de crisis y los módulos con el framework Octalysis adaptado a tu sector.', en: 'We write the script, crisis scenarios, and modules using the Octalysis framework adapted to your industry.' },
                },
                {
                  step: '03',
                  icon: Layers,
                  color: '#8b5cf6',
                  title: { es: 'Producción & Carga en la Plataforma', en: 'Production & Platform Upload' },
                  desc: { es: 'El curso queda activo en tu instancia Marca Blanca con evaluación final y emisión automática de Open Badges 3.0.', en: 'The course goes live in your White-Label instance with final assessment and automatic Open Badges 3.0 issuance.' },
                },
                {
                  step: '04',
                  icon: Award,
                  color: '#10b981',
                  title: { es: 'Credencial Verificable Personalizada', en: 'Custom Verifiable Credential' },
                  desc: { es: 'Emitimos insignias W3C Open Badges 3.0 firmadas con el sello y nombre de curso específico de tu empresa.', en: 'We issue W3C Open Badges 3.0 signed with your company seal and the specific course name.' },
                },
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: '44px', height: '44px', borderRadius: '12px', background: `${step.color}22`, border: `1px solid ${step.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <step.icon size={20} color={step.color} />
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 800, color: step.color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{t('Paso', 'Step')} {step.step}</div>
                    <h4 style={{ fontSize: '15px', fontWeight: 800, marginBottom: '6px', color: 'var(--text-primary)' }}>{step.title[lang]}</h4>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>{step.desc[lang]}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA card */}
            <div className="glass-card" style={{ padding: '36px', borderRadius: 'var(--radius-lg)', border: '1px solid rgba(239, 68, 68, 0.25)', background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, rgba(245, 158, 11, 0.06) 100%)', position: 'sticky', top: '80px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                <Pencil size={26} color="#f87171" />
              </div>
              <h3 style={{ fontSize: '22px', fontWeight: 900, marginBottom: '12px', letterSpacing: '-0.02em' }}>
                {t('¿Listo para tu Primer Curso a Medida?', 'Ready for Your First Custom Course?')}
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.65, marginBottom: '24px' }}>
                {t(
                  'Cuéntanos qué necesitan aprender tus empleados. Nuestro equipo de diseño instruccional te entrega una propuesta en 48 horas.',
                  'Tell us what your employees need to learn. Our instructional design team delivers a proposal within 48 hours.'
                )}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                {[
                  { es: 'Procesos internos y onboarding empresarial', en: 'Internal processes & corporate onboarding' },
                  { es: 'Normativas sectoriales específicas (salud, finanzas, construcción)', en: 'Sector-specific regulations (health, finance, construction)' },
                  { es: 'Políticas internas de ética y gobierno corporativo', en: 'Internal ethics and corporate governance policies' },
                  { es: 'Habilidades técnicas y certificaciones industriales', en: 'Technical skills & industrial certifications' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    <CheckCircle2 size={14} color="#10b981" style={{ marginTop: '2px', flexShrink: 0 }} />
                    {item[lang]}
                  </div>
                ))}
              </div>
              <button
                className="btn btn-primary"
                style={{ width: '100%', padding: '14px 20px', fontSize: '14px', fontWeight: 800, borderRadius: '10px', background: 'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)', boxShadow: '0 0 24px rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                onClick={() => {
                  setContactService('custom-course');
                  setIsContactOpen(true);
                }}
              >
                <Pencil size={16} />
                {t('Solicitar Propuesta de Curso', 'Request Course Proposal')}
                <ArrowRight size={16} />
              </button>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '12px' }}>
                {t('Respuesta en < 48h · Propuesta sin compromiso', 'Response in < 48h · No-commitment proposal')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA BANNER ─────────────────────────────────────────────── */}
      <section
        style={{
          padding: '80px 24px',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(139, 92, 246, 0.12) 100%)',
          borderTop: '1px solid var(--border-color)',
          textAlign: 'center',
        }}
      >
        <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 40px)', fontWeight: 900, lineHeight: 1.2, letterSpacing: '-0.025em', marginBottom: '16px' }}>
          {lang === 'es' ? '¿Listo para Certificar a tu Equipo Hoy?' : 'Ready to Certify Your Team Today?'}
        </h2>
        <p style={{ fontSize: '15px', color: 'var(--text-secondary)', maxWidth: '480px', margin: '0 auto 32px', lineHeight: 1.55 }}>
          {t(
            'Comienza en minutos. Onboarding corporativo B2B con carga CSV y credenciales verificables desde el día uno.',
            'Get started in minutes. B2B corporate onboarding with CSV upload and verifiable credentials from day one.'
          )}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            style={{ padding: '16px 32px', fontSize: '16px', fontWeight: 800, borderRadius: '12px', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', boxShadow: '0 0 28px rgba(59, 130, 246, 0.35)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            onClick={() => onGoToAuth('signup')}
          >
            {t('Crear Cuenta Gratis', 'Create Free Account')}
            <ArrowRight size={16} />
          </button>
          <button
            className="btn btn-secondary"
            style={{ padding: '16px 24px', fontSize: '15px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            onClick={() => onGoToAuth('signin')}
          >
            {t('Ya tengo cuenta', 'I have an account')}
          </button>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid var(--border-color)', padding: '32px 24px', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--gradient-brand)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={16} color="#fff" />
            </div>
            <span style={{ fontWeight: 800, fontSize: '14px', color: 'var(--text-primary)' }}>
              e-learning <span style={{ color: 'var(--accent-primary)', fontWeight: 600, fontSize: '11px' }}>by akretrix</span>
            </span>
          </div>

          <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: 'var(--text-muted)' }}>
            <span>{t('Cumplimiento Corporativo LATAM & Global', 'LATAM & Global Corporate Compliance')}</span>
            <span>·</span>
            <span style={{ color: '#06b6d4', cursor: 'pointer' }} onClick={() => {
              setContactService('elearning-platform');
              setIsContactOpen(true);
            }}>
              {t('Contacto & Soporte', 'Contact & Support')}
            </span>
            <span>·</span>
            <span style={{ color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => setIsPrivacyOpen(true)}>
              {t('Privacidad y Datos', 'Privacy & Data')}
            </span>
            <span>·</span>
            <span style={{ color: '#60a5fa', cursor: 'pointer' }} onClick={() => navigate('/verify')}>
              {t('Verificar Insignia', 'Verify Badge')}
            </span>
          </div>
        </div>
      </footer>

      {/* ── SHARED TRANSVERSAL MODALS (@joxpg/akretrix-ui) ────────────────── */}
      <ContactModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        source="elearning"
        defaultService={contactService}
        lang={lang as 'en' | 'es'}
        apiEndpoint="https://api.akretrix.com/contact"
        turnstileSiteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
        onSuccess={() => {
          console.log('[akretrix-elearning] Contact lead submitted successfully');
        }}
      />

      <PrivacyPolicyModal
        isOpen={isPrivacyOpen}
        onClose={() => setIsPrivacyOpen(false)}
        lang={lang as 'en' | 'es'}
      />
    </div>
  );
};
